import { Media } from "./Media";
import { trackDemoClick } from "@/lib/telemetry";
import { useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "./ui/navigation-menu";
import logoAsset from "@/assets/datasyteAI-logo-black.png.asset.json";
const logo = logoAsset.url;

type NavLink = { href: string; label: string; description?: string };

const platformLinks: NavLink[] = [
  { href: "/how-it-works", label: "How it Works", description: "The Code Blue workflow, step by step." },
  { href: "/aimediq", label: "AIMediQ™", description: "The AI-powered Code Cart." },
  { href: "/technology", label: "Technology", description: "Product & technology overview." },
  { href: "/train", label: "Train", description: "Trained to your experts and protocols." },
  { href: "/benefits", label: "Benefits", description: "What improves with datasyteAI™." },
  { href: "/video", label: "Video", description: "What is a Code Blue? A quick explainer." },
];

const trustLinks: NavLink[] = [
  { href: "/problem", label: "Problem", description: "Why Code Blue outcomes vary." },
  { href: "/guardrails", label: "Guardrails", description: "Security, privacy, and safety limits." },
  { href: "/regulatory-status", label: "Regulatory Status", description: "Pre-market development & validation." },
  { href: "/disclaimers", label: "Disclaimers", description: "Full legal and regulatory disclaimers." },
];

const investorLinks: NavLink[] = [
  { href: "/market", label: "Market", description: "Market size and opportunity." },
  { href: "/business-model", label: "Model", description: "Business model & financial strategy." },
  { href: "/competition", label: "Competition", description: "How we compare." },
  { href: "https://pathfree.com/investors", label: "More information", description: "Visit the PathFree investor page." },
];

const allMobileLinks: NavLink[] = [
  ...platformLinks,
  ...trustLinks,
  ...investorLinks,
  { href: "/flip-book", label: "Interactive Flip Book" },
  { href: "/contact", label: "Contact" },
];

const isExternal = (href: string) => href.startsWith("http://") || href.startsWith("https://");

const DropdownList = ({ links }: { links: NavLink[] }) => (
  <ul className="grid w-[320px] gap-1 p-3 md:w-[420px] md:grid-cols-2">
    {links.map((link) => {
      const external = isExternal(link.href);
      return (
        <li key={link.href}>
          <NavigationMenuLink asChild>
            {external ? (
              <a
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="block select-none rounded-lg p-3 leading-none no-underline outline-none transition-colors hover:bg-accent/10 focus:bg-accent/10"
              >
                <div className="text-sm font-semibold text-foreground">{link.label}</div>
                {link.description && (
                  <p className="mt-1 line-clamp-2 text-xs leading-snug text-muted-foreground">
                    {link.description}
                  </p>
                )}
              </a>
            ) : (
              <Link
                to={link.href}
                className="block select-none rounded-lg p-3 leading-none no-underline outline-none transition-colors hover:bg-accent/10 focus:bg-accent/10"
              >
                <div className="text-sm font-semibold text-foreground">{link.label}</div>
                {link.description && (
                  <p className="mt-1 line-clamp-2 text-xs leading-snug text-muted-foreground">
                    {link.description}
                  </p>
                )}
              </Link>
            )}
          </NavigationMenuLink>
        </li>
      );
    })}
  </ul>
);

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 bg-background/90 backdrop-blur-md border-b border-border">
      <div className="container mx-auto px-6 py-4">
        <div className="grid grid-cols-[auto_1fr_auto] gap-4 items-center">
          <div className="flex items-center gap-3">
            <Media
              src={logo}
              alt="datasyteAI™ by PathFree Technologies logo"
              className="h-14 w-auto object-contain"
              sizes="230px"
              eager
            />

          </div>

          <div className="hidden lg:flex justify-end">
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link
                      to="/"
                      className="inline-flex h-10 items-center rounded-md px-4 text-sm font-medium text-foreground transition-colors hover:text-accent"
                    >
                      Home
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuTrigger className="text-sm font-medium bg-transparent">
                    Platform
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <DropdownList links={platformLinks} />
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuTrigger className="text-sm font-medium bg-transparent">
                    Trust
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <DropdownList links={trustLinks} />
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuTrigger className="text-sm font-medium bg-transparent">
                    Investors
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <DropdownList links={investorLinks} />
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link
                      to="/contact"
                      className="inline-flex h-10 items-center rounded-md px-4 text-sm font-medium text-foreground transition-colors hover:text-accent"
                    >
                      Contact
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          <div className="hidden lg:block">
            <Button asChild>
              <a href="https://pathfree.com/contact" onClick={() => trackDemoClick("header_desktop")}>
                Schedule a Demo
              </a>
            </Button>
          </div>

          <button
            className="lg:hidden col-start-3 justify-self-end inline-flex items-center justify-center min-h-12 min-w-12 text-2xl text-foreground"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            <Menu />
          </button>
        </div>

        {isMenuOpen && (
          <nav className="lg:hidden flex flex-col gap-3 mt-4 pb-4">
            <Link
              to="/"
              className="text-sm font-medium text-foreground hover:text-accent transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              Home
            </Link>
            {allMobileLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="text-sm font-medium text-foreground hover:text-accent transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <Button asChild className="w-full mt-2">
              <a href="https://pathfree.com/contact" onClick={() => trackDemoClick("header_mobile")}>
                Schedule a Demo
              </a>
            </Button>
          </nav>
        )}
      </div>
    </header>
  );
};

